import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getCampaign } from "../../apis/campaign";
import {
    normalizeCampaign,
    getDisplayLanguage,
    getDisplayPlatform,
    getCampaignPlatformIcon,
    isCampaignDraft,
} from "../../utils/normalizeCampaign";
import { APP_ROUTES, buildCampaignWizardPath } from "../../constants/appRoutes";
import PostCard from "../shared/PostCard";
import { DividedList, DividedListItem } from "../shared/ContentList";
import SelectedOpportunityPreview from "./wizard/SelectedOpportunityPreview";
import { PageShell } from "../shared/PageHeader";
import CampaignDetailsSkeletons from "./CampaignDetailsSkeletons";
import "bootstrap-icons/font/bootstrap-icons.css";

function MetaItem({ icon, iconClass = "text-slate-400", children }) {
    if (!children) return null;

    return (
        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
            <i className={`bi ${icon} text-sm ${iconClass}`} aria-hidden />
            {children}
        </span>
    );
}

function SectionLabel({ icon, children }) {
    return (
        <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 md:gap-2.5 md:text-sm md:tracking-[0.15em]">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700 md:h-8 md:w-8">
                <i className={`bi ${icon} text-sm md:text-base`} aria-hidden />
            </span>
            {children}
        </p>
    );
}

function CampaignSummary({ campaign }) {
    const { name, followUp } = campaign;
    const platform = getDisplayPlatform(campaign);
    const language = getDisplayLanguage(campaign);
    const platformIcon = getCampaignPlatformIcon(campaign);

    return (
        <div className="mb-4 min-w-0 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="mb-0 min-w-0 flex-1 break-words text-lg font-bold text-slate-900 md:text-xl">
                    {name}
                </h1>
                {isCampaignDraft(campaign) ? (
                    <Link
                        to={buildCampaignWizardPath(null, campaign.id)}
                        className="btn btn-dark-app shrink-0 px-4 py-2 text-sm font-semibold no-underline"
                    >
                        <i className="bi bi-arrow-right-circle me-2" aria-hidden />
                        Continue
                    </Link>
                ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {platform ? (
                    <MetaItem icon={platformIcon.icon} iconClass={platformIcon.iconClass}>
                        {platform}
                    </MetaItem>
                ) : null}
                {language ? <MetaItem icon="bi-translate">{language}</MetaItem> : null}
                {followUp.tone ? <MetaItem icon="bi-chat-heart">{followUp.tone}</MetaItem> : null}
                {followUp.target ? <MetaItem icon="bi-bullseye">{followUp.target}</MetaItem> : null}
                {followUp.topic ? <MetaItem icon="bi-lightbulb">{followUp.topic}</MetaItem> : null}
            </div>

            {followUp.hashtags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {followUp.hashtags.map((tag, idx) => (
                        <span className="text-xs text-slate-500" key={`${tag}-${idx}`}>
                            {tag}
                        </span>
                    ))}
                </div>
            ) : null}

            {followUp.opportunities?.length > 0 ? (
                <SelectedOpportunityPreview opportunities={followUp.opportunities} readOnly />
            ) : null}
        </div>
    );
}

const Details = () => {
    const { id } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function loadCampaign() {
            setLoading(true);
            setError(null);

            try {
                const data = await getCampaign(id);
                if (!cancelled) {
                    setCampaign(normalizeCampaign(data));
                }
            } catch (err) {
                if (!cancelled) {
                    const message =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Failed to load campaign. Please try again.";
                    setError(message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        if (id) {
            loadCampaign();
        }

        return () => {
            cancelled = true;
        };
    }, [id]);

    const copyText = async (text, message) => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            toast(message);
        } catch {
            toast.error("Unable to copy right now");
        }
    };

    if (loading) {
        return <CampaignDetailsSkeletons />;
    }

    if (error) {
        return (
            <PageShell>
                <nav className="mb-3" aria-label="Breadcrumb">
                    <ol className="mb-0 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                        <li>
                            <Link to={APP_ROUTES.CAMPAIGNS} className="hover:text-slate-900">
                                My Campaigns
                            </Link>
                        </li>
                        <li aria-hidden>
                            <i className="bi bi-chevron-right text-[10px]" />
                        </li>
                        <li className="text-slate-900">Campaign details</li>
                    </ol>
                </nav>
                <p className="text-sm font-medium text-red-600">Could not load campaign</p>
                <p className="mt-1 text-sm text-red-500">{error}</p>
            </PageShell>
        );
    }

    const { posts } = campaign;

    return (
        <PageShell>
            <nav className="mb-3" aria-label="Breadcrumb">
                <ol className="mb-0 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <li>
                        <Link to={APP_ROUTES.CAMPAIGNS} className="hover:text-slate-900">
                            My Campaigns
                        </Link>
                    </li>
                    <li aria-hidden>
                        <i className="bi bi-chevron-right text-[10px]" />
                    </li>
                    <li className="min-w-0 break-words text-slate-900">{campaign.name}</li>
                </ol>
            </nav>

            <CampaignSummary campaign={campaign} />

            <div className="min-w-0 w-full">
                <SectionLabel icon="bi-file-earmark-text-fill">
                    Posts ({posts.length})
                </SectionLabel>

                {posts.length === 0 ? (
                    <p className="text-sm text-slate-500">No posts in this campaign.</p>
                ) : (
                    <DividedList>
                        {posts.map((post, idx) => (
                            <DividedListItem key={post.id ?? idx}>
                                <PostCard post={post} onCopy={copyText} />
                            </DividedListItem>
                        ))}
                    </DividedList>
                )}
            </div>
        </PageShell>
    );
};

export default Details;
